// 瀏覽器端帳號：訪客登入憑證、呼叫帳號與牌局組 API、把伺服器快照寫回本機存檔快取
// 對應 docs/Account-Ledger-Design.md
//
// - 第一次開啟自動建立訪客帳號（伺服器發新手禮），憑證存在 localStorage
// - 本機存檔（mahjongking.save.v1）只是快取：每次 API 回來就用伺服器快照覆寫，畫面照舊讀存檔
// - 金幣、每日次數、牌局組的變動一律由伺服器決定
//
// === 使用圖片 ===
// 無 — 純連線

import type { TTableId } from '@core/table-catalog';
import type { TShopItemId } from './shop-catalog';
import { createDefaultGameSave, saveGameSave, type IGameSave } from '@storage/game-save';
import type { IAccountSnapshot, IRoundSettlement } from './account-service';

const CREDENTIAL_KEY = 'mahjongking.account';
const REQUEST_TIMEOUT_MS = 15_000; // 單一請求逾時

interface ICredential {
  readonly accountId: string;
  readonly token: string;
}

export class CApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

let snapshotCache: IAccountSnapshot | null = null;
const listeners = new Set<(snapshot: IAccountSnapshot) => void>();

const readCredential = (): ICredential | null => {
  try {
    const raw = localStorage.getItem(CREDENTIAL_KEY);
    return raw ? (JSON.parse(raw) as ICredential) : null;
  } catch {
    return null;
  }
};

const writeCredential = (credential: ICredential | null): void => {
  try {
    if (credential) localStorage.setItem(CREDENTIAL_KEY, JSON.stringify(credential));
    else localStorage.removeItem(CREDENTIAL_KEY);
  } catch {
    /* 無痕模式寫不進去：本次可玩，下次開啟會是新帳號 */
  }
};

const rawPost = async <T>(path: string, body: unknown, credential: ICredential | null) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(path, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(credential
          ? { authorization: `Bearer ${credential.accountId}.${credential.token}` }
          : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const data = (await response.json().catch(() => ({}))) as { error?: string };
    if (!response.ok) throw new CApiError(data.error ?? '伺服器錯誤', response.status);
    return data as T;
  } finally {
    clearTimeout(timer);
  }
};

/** 伺服器快照 → 本機存檔快取（瀏覽器存檔格式不變，畫面照舊讀取）。 */
export const applySnapshot = (snapshot: IAccountSnapshot): void => {
  snapshotCache = snapshot;
  const base = createDefaultGameSave();
  const save: IGameSave = {
    ...base,
    ledger: { ...base.ledger, balances: [...snapshot.balances], appliedTransactionIds: [] },
    activeSession: snapshot.session,
  };
  saveGameSave(save);
  for (const listener of listeners) listener(snapshot);
};

/** 最近一次的帳號快照（尚未連線過為 null）。 */
export const accountSnapshot = (): IAccountSnapshot | null => snapshotCache;

export const onAccountChange = (listener: (snapshot: IAccountSnapshot) => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const createGuest = async (): Promise<IAccountSnapshot> => {
  const created = await rawPost<{ accountId: string; token: string; snapshot: IAccountSnapshot }>(
    '/api/account/guest',
    {},
    null
  );
  writeCredential({ accountId: created.accountId, token: created.token });
  applySnapshot(created.snapshot);
  return created.snapshot;
};

/** 帶登入憑證呼叫 API；沒有帳號就先建立訪客帳號。 */
export const apiPost = async <T>(path: string, body: unknown = {}): Promise<T> => {
  let credential = readCredential();
  if (!credential) {
    await createGuest();
    credential = readCredential();
  }
  return rawPost<T>(path, body, credential);
};

/** 開啟遊戲時呼叫：取得（必要時建立）帳號並同步本機快取。 */
export const refreshAccount = async (): Promise<IAccountSnapshot> => {
  if (!readCredential()) return createGuest();
  try {
    const snapshot = await apiPost<IAccountSnapshot>('/api/account/me');
    applySnapshot(snapshot);
    return snapshot;
  } catch (error) {
    // 憑證失效（例如本機開發重啟伺服器）：改建新的訪客帳號
    if (error instanceof CApiError && (error.status === 401 || error.status === 409)) {
      writeCredential(null);
      return createGuest();
    }
    throw error;
  }
};

export const signIn = async (): Promise<number> => {
  const r = await apiPost<{ reward: number; snapshot: IAccountSnapshot }>('/api/account/sign-in');
  applySnapshot(r.snapshot);
  return r.reward;
};

export const claimRelief = async (): Promise<number> => {
  const r = await apiPost<{ reward: number; snapshot: IAccountSnapshot }>('/api/account/relief');
  applySnapshot(r.snapshot);
  return r.reward;
};

export const startSet = async (tableId: TTableId, planIndex: number): Promise<void> => {
  applySnapshot(await apiPost<IAccountSnapshot>('/api/game/start', { tableId, planIndex }));
};

/** 準備下一手；上一手若中途離開，伺服器會先代打完並結算（autoFinished）。 */
export const nextRound = async (planIndex?: number): Promise<IRoundSettlement | null> => {
  const r = await apiPost<{ snapshot: IAccountSnapshot; autoFinished: IRoundSettlement | null }>(
    '/api/game/next',
    planIndex === undefined ? {} : { planIndex }
  );
  applySnapshot(r.snapshot);
  return r.autoFinished;
};

export const abandonSet = async (): Promise<IRoundSettlement | null> => {
  const r = await apiPost<{ snapshot: IAccountSnapshot; autoFinished: IRoundSettlement | null }>(
    '/api/game/abandon'
  );
  applySnapshot(r.snapshot);
  return r.autoFinished;
};

export interface IHelpRequestResult {
  readonly url: string; // 親友打開的代買頁
  readonly shareUrl: string; // LINE 分享網址
}

/** 請人幫忙付：伺服器產生有時效的代買連結，回傳 LINE 分享網址。 */
export const requestHelp = (itemId: TShopItemId, reason: string): Promise<IHelpRequestResult> =>
  apiPost<IHelpRequestResult>('/api/help/request', { itemId, reason });

// ---- LINE Login ----

const LOGIN_FRAGMENT = '#login=';
const LOGIN_ERROR_FRAGMENT = '#login-error=';
const LOGIN_STARTED_KEY = 'mahjongking.line-login-started'; // 這個瀏覽器自己發起登入的時間
const LOGIN_STARTED_TTL_MS = 15 * 60 * 1000; // 發起後多久內接受導回

export const LoginResult = {
  None: 'none',
  Success: 'success',
  Failed: 'failed',
} as const;
export type TLoginResult = (typeof LoginResult)[keyof typeof LoginResult];

/**
 * LINE 登入導回後，網址 # 後面帶著新憑證（不會送到伺服器）：存起來並從網址清掉。
 * 程式一開始就要呼叫，之後的 API 才會用新帳號。
 */
export const consumeLoginFragment = (): TLoginResult => {
  if (typeof location === 'undefined') return LoginResult.None;
  const hash = location.hash;
  const clear = (): void =>
    history.replaceState(null, '', `${location.pathname}${location.search}`);
  if (hash.startsWith(LOGIN_FRAGMENT)) {
    const [accountId, token] = hash.slice(LOGIN_FRAGMENT.length).split('.');
    clear();
    // 只接受這個瀏覽器自己發起的登入：別人傳來帶憑證的連結不能把人換進別的帳號
    let started: number;
    try {
      started = Number(localStorage.getItem(LOGIN_STARTED_KEY) ?? 0);
      localStorage.removeItem(LOGIN_STARTED_KEY);
    } catch {
      started = 0;
    }
    if (!accountId || !token || Date.now() - started > LOGIN_STARTED_TTL_MS)
      return LoginResult.Failed;
    writeCredential({ accountId, token });
    return LoginResult.Success;
  }
  if (hash.startsWith(LOGIN_ERROR_FRAGMENT)) {
    clear();
    return LoginResult.Failed;
  }
  return LoginResult.None;
};

/** LINE 登入是否開放（伺服器有設定 channel）。 */
export const lineLoginEnabled = async (): Promise<boolean> => {
  try {
    const r = await rawPost<{ enabled: boolean }>('/api/auth/line/status', {}, null);
    return r.enabled;
  } catch {
    return false;
  }
};

/** 開始 LINE 登入：伺服器產生綁定目前帳號的授權網址，整頁前往 LINE。 */
export const startLineLogin = async (): Promise<void> => {
  const { url } = await apiPost<{ url: string }>('/api/auth/line/start');
  try {
    localStorage.setItem(LOGIN_STARTED_KEY, String(Date.now()));
  } catch {
    /* 寫不進去的話導回時會被當成無效連結，請長輩再按一次 */
  }
  location.href = url;
};
